'use client'

import { type Hit } from 'instantsearch.js'
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react'

import { type AlgoliaProxyRecord } from '../../../../algolia'
import { useGlobalConfigContext } from '../../../../global-config'
import { TENANTS_WITH_MARKETPLACE_FUNCTIONALITY } from '../../../../marketplace'
import {
  type ProductSearchResultItemNowDetails,
  useProductSearchResultItemNowDetails,
} from '../useProductSearchResultItemNowDetails'

type ProductSearchResultItemValue = {
  algoliaRecord: Hit<AlgoliaProxyRecord>
  isMarketplace: boolean
  nowDetails: ProductSearchResultItemNowDetails
  onProductClick?: () => void
  query?: string
}

const ProductSearchResultItemContext =
  createContext<ProductSearchResultItemValue | null>(null)

type ProductSearchResultItemContextProviderProps = PropsWithChildren<{
  algoliaRecord: Hit<AlgoliaProxyRecord>
  onProductClick?: () => void
  query?: string
}>

export const ProductSearchResultItemContextProvider: FC<
  ProductSearchResultItemContextProviderProps
> = ({ algoliaRecord, children, onProductClick, query }) => {
  const globalConfig = useGlobalConfigContext()

  const isMarketplace = TENANTS_WITH_MARKETPLACE_FUNCTIONALITY.has(
    globalConfig.tenant,
  )

  const nowDetails = useProductSearchResultItemNowDetails({
    currentVariant: algoliaRecord,
    tenant: globalConfig.tenant,
  })

  const value = useMemo(
    () => ({
      algoliaRecord,
      isMarketplace,
      nowDetails,
      onProductClick,
      query,
    }),
    [algoliaRecord, isMarketplace, nowDetails, onProductClick, query],
  )

  return (
    <ProductSearchResultItemContext.Provider value={value}>
      {children}
    </ProductSearchResultItemContext.Provider>
  )
}

export const useProductSearchResultItemContext =
  (): ProductSearchResultItemValue => {
    const context = useContext(ProductSearchResultItemContext)

    if (context === null) {
      throw new Error(
        'useProductSearchResultItemContext must be used within a ProductSearchResultItemContextProvider',
      )
    }

    return context
  }
